/**
 * The spatial Journey.
 *
 * Routes split from the origin, cross at dependency gates and rejoin at
 * integration and test stations. Node positions come from the projection, so
 * this is a real route network rather than phase columns: a station can sit
 * above or below the route that feeds it, and two routes can converge on the
 * same node from opposite directions.
 *
 * Hierarchy: origin, destination and testable stations are largest; merge and
 * dependency gates next; slices smallest. The critical path is drawn heavier.
 * Everything here has a keyboard-reachable equivalent in `JourneyList`.
 */
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { JourneyEdgeView, JourneyNodeView, JourneyView } from '@dcc/shared';
import { toneFor } from '../lib/tone';

const VIEW_W = 1320;
const VIEW_H = 660;
const PAD_X = 60;
const PAD_Y = 56;

interface Point {
  x: number;
  y: number;
}

function project(node: JourneyNodeView): Point {
  return {
    x: PAD_X + (node.x / 100) * (VIEW_W - PAD_X * 2),
    y: PAD_Y + (node.y / 100) * (VIEW_H - PAD_Y * 2),
  };
}

interface NodeGeometry {
  rx: number;
  ry: number;
  shape: 'rect' | 'hex' | 'circle';
  /** Origin and destination carry their label outside the shape. */
  labelOutside: boolean;
  lines: string[];
}

/**
 * Node geometry follows its label: a two-line title makes the shape taller
 * rather than spilling over its own border.
 */
function sizeFor(node: JourneyNodeView): NodeGeometry {
  const base = ((): Omit<NodeGeometry, 'lines'> => {
    switch (node.type) {
      case 'TESTABLE_STATION':
        return { rx: 62, ry: 34, shape: 'rect', labelOutside: false };
      case 'ORIGIN':
      case 'DESTINATION':
        return { rx: 30, ry: 30, shape: 'circle', labelOutside: true };
      case 'ENVIRONMENT':
      case 'RELEASE_CANDIDATE':
        return { rx: 54, ry: 30, shape: 'rect', labelOutside: false };
      case 'INTEGRATION':
        return { rx: 50, ry: 30, shape: 'hex', labelOutside: false };
      case 'DEPENDENCY_GATE':
      case 'DECISION':
      case 'HUMAN_HANDOFF':
        return { rx: 52, ry: 28, shape: 'hex', labelOutside: false };
      default:
        return { rx: 48, ry: 26, shape: 'rect', labelOutside: false };
    }
  })();

  if (base.labelOutside) return { ...base, lines: wrap(node.title, 16) };

  const budget = base.shape === 'hex' ? base.rx / 5.4 : base.rx / 4.6;
  const lines = wrap(node.title, budget);
  const needed =
    (node.type === 'TESTABLE_STATION' ? 14 : 0) +
    lines.length * 16 +
    14 +
    (base.shape === 'hex' ? 10 : 6);
  return { ...base, lines, ry: Math.max(base.ry, Math.ceil(needed / 2)) };
}

function edgeClass(edge: JourneyEdgeView): string {
  const base =
    edge.status === 'BLOCKED'
      ? 'edge edge--blocked'
      : edge.status === 'COMPLETE'
        ? 'edge edge--complete'
        : edge.status === 'ACTIVE'
          ? 'edge edge--active'
          : 'edge edge--planned';
  const kind =
    edge.kind === 'DEPENDENCY'
      ? ' edge--dependency'
      : edge.kind === 'HANDOFF'
        ? ' edge--handoff'
        : '';
  return `${base}${kind}${edge.onCriticalPath ? ' edge--critical' : ''}`;
}

/** A curved connector so crossing routes stay readable where they overlap. */
function path(from: Point, to: Point): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const bend = Math.max(28, Math.abs(dx) * 0.42);
  if (Math.abs(dy) < 6) return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  return `M ${from.x} ${from.y} C ${from.x + bend} ${from.y}, ${to.x - bend} ${to.y}, ${to.x} ${to.y}`;
}

export function JourneyCanvas({
  journey,
  selectedId,
  onSelect,
  routeFilter,
  focusCriticalPath,
}: {
  journey: JourneyView;
  selectedId: string | null;
  onSelect: (nodeId: string) => void;
  routeFilter: string | null;
  focusCriticalPath: boolean;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const dragging = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  const points = useMemo(() => {
    const map = new Map<string, Point>();
    for (const node of journey.nodes) map.set(node.id, project(node));
    return map;
  }, [journey.nodes]);

  const visible = useMemo(() => {
    const ids = new Set(
      journey.nodes
        .filter((node) => {
          if (routeFilter && node.route !== routeFilter) return false;
          if (focusCriticalPath && !node.onCriticalPath) return false;
          return true;
        })
        .map((node) => node.id),
    );
    return ids;
  }, [journey.nodes, routeFilter, focusCriticalPath]);

  const viewBox = `${-pan.x} ${-pan.y} ${VIEW_W / zoom} ${VIEW_H / zoom}`;

  function onKeyDown(event: KeyboardEvent<SVGSVGElement>) {
    const step = 40 / zoom;
    if (event.key === 'ArrowRight') setPan((p) => ({ ...p, x: p.x - step }));
    else if (event.key === 'ArrowLeft') setPan((p) => ({ ...p, x: p.x + step }));
    else if (event.key === 'ArrowDown') setPan((p) => ({ ...p, y: p.y - step }));
    else if (event.key === 'ArrowUp') setPan((p) => ({ ...p, y: p.y + step }));
    else if (event.key === '+' || event.key === '=') setZoom((z) => Math.min(2.6, z + 0.2));
    else if (event.key === '-') setZoom((z) => Math.max(0.6, z - 0.2));
    else if (event.key === '0') {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } else return;
    event.preventDefault();
  }

  return (
    <div className="journey">
      <div className="journey__toolbar">
        <span className="label">Route map</span>
        <span className="small dim">{journey.nodes.length} nodes</span>
        <span style={{ marginLeft: 'auto' }} className="inline inline--tight">
          <button
            type="button"
            className="btn btn--sm btn--quiet"
            onClick={() => setZoom((z) => Math.max(0.6, z - 0.2))}
            aria-label="Zoom out"
          >
            −
          </button>
          <span className="mono tiny" style={{ minWidth: 42, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            className="btn btn--sm btn--quiet"
            onClick={() => setZoom((z) => Math.min(2.6, z + 0.2))}
            aria-label="Zoom in"
          >
            +
          </button>
          <button
            type="button"
            className="btn btn--sm btn--quiet"
            onClick={() => {
              setZoom(1);
              setPan({ x: 0, y: 0 });
            }}
          >
            Fit
          </button>
        </span>
      </div>

      <svg
        ref={svgRef}
        className="journey__canvas"
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Journey map for ${journey.projectName}. ${journey.nodes.length} nodes across ${journey.routes.length} routes. An equivalent ordered list follows this map.`}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerDown={(event) => {
          dragging.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y };
          (event.target as Element).setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!dragging.current) return;
          const scale = VIEW_W / (svgRef.current?.clientWidth ?? VIEW_W) / zoom;
          setPan({
            x: dragging.current.panX + (event.clientX - dragging.current.x) * scale,
            y: dragging.current.panY + (event.clientY - dragging.current.y) * scale,
          });
        }}
        onPointerUp={() => {
          dragging.current = null;
        }}
        onPointerLeave={() => {
          dragging.current = null;
        }}
      >
        <defs>
          <marker
            id="arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="5"
            markerHeight="5"
            orient="auto"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" fill="currentColor" opacity="0.7" />
          </marker>
        </defs>

        <g aria-hidden="true">
          {journey.edges.map((edge) => {
            const from = points.get(edge.from);
            const to = points.get(edge.to);
            if (!from || !to) return null;
            const dim = !visible.has(edge.from) || !visible.has(edge.to);
            const fromNode = journey.nodes.find((n) => n.id === edge.from)!;
            const toNode = journey.nodes.find((n) => n.id === edge.to)!;
            const fromSize = sizeFor(fromNode);
            const toSize = sizeFor(toNode);
            const start = { x: from.x + fromSize.rx, y: from.y };
            const end = { x: to.x - toSize.rx, y: to.y };
            return (
              <path
                key={edge.id}
                className={edgeClass(edge)}
                d={path(start, end)}
                opacity={dim ? 0.18 : 1}
                markerEnd="url(#arrow)"
              />
            );
          })}
        </g>

        {journey.nodes.map((node) => {
          const point = points.get(node.id)!;
          const size = sizeFor(node);
          const tone = toneFor('workItem', node.status);
          const dim = !visible.has(node.id);
          const isStation = node.type === 'TESTABLE_STATION';
          const classes = [
            'node',
            `node--${node.type.toLowerCase().replace(/_/g, '-')}`,
            isStation ? 'node--station' : '',
            node.type === 'DESTINATION' ? 'node--destination' : '',
            node.type === 'ORIGIN' ? 'node--origin' : '',
            node.onCriticalPath ? 'node--critical' : '',
            selectedId === node.id ? 'node--selected' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <g
              key={node.id}
              className={classes}
              data-tone={tone}
              data-testid={`journey-node-${node.id}`}
              transform={`translate(${point.x} ${point.y})`}
              opacity={dim ? 0.2 : 1}
              onClick={() => onSelect(node.id)}
              role="button"
              tabIndex={-1}
              aria-label={`${node.title}, ${node.status}`}
            >
              {node.isCurrentPosition && (
                <ellipse className="node__current" rx={size.rx + 9} ry={size.ry + 9} />
              )}
              {size.shape === 'circle' ? (
                <circle className="node__shape" r={size.rx} />
              ) : size.shape === 'hex' ? (
                <polygon
                  className="node__shape"
                  points={`${-size.rx},0 ${-size.rx * 0.6},${-size.ry} ${size.rx * 0.6},${-size.ry} ${size.rx},0 ${size.rx * 0.6},${size.ry} ${-size.rx * 0.6},${size.ry}`}
                />
              ) : (
                <rect
                  className="node__shape"
                  x={-size.rx}
                  y={-size.ry}
                  width={size.rx * 2}
                  height={size.ry * 2}
                  rx={10}
                />
              )}

              {(() => {
                // The label block is centred inside the shape, or placed under
                // it for origin and destination, and never spills over a border.
                const badgeHeight = isStation ? 14 : 0;
                const blockHeight = badgeHeight + size.lines.length * 16 + 14;
                const top = size.labelOutside ? size.ry + 16 : -blockHeight / 2;
                return (
                  <>
                    {isStation && (
                      <text className="node__badge" y={top + 10} textAnchor="middle">
                        TEST STATION
                      </text>
                    )}
                    {size.lines.map((line, index) => (
                      <text
                        key={line}
                        className="node__title"
                        y={top + badgeHeight + 12 + index * 16}
                        textAnchor="middle"
                      >
                        {line}
                      </text>
                    ))}
                    <text
                      className="node__meta"
                      y={top + badgeHeight + size.lines.length * 16 + 11}
                      textAnchor="middle"
                    >
                      {node.status.replace(/_/g, ' ').toLowerCase()}
                    </text>
                  </>
                );
              })()}

              {node.agents.length > 0 && (
                <g transform={`translate(${size.rx - 6} ${-size.ry - 2})`}>
                  <circle className="node__agent" r={7} />
                  <text
                    className="node__meta"
                    y={3}
                    textAnchor="middle"
                    style={{ fill: 'var(--bg-0)', fontWeight: 700 }}
                  >
                    {node.agents.length}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      <div className="journey__legend">
        <span className="journey__legend-item" data-tone="success">
          <span className="journey__legend-swatch" /> completed and verified
        </span>
        <span className="journey__legend-item" data-tone="active">
          <span className="journey__legend-swatch" /> in progress
        </span>
        <span className="journey__legend-item" data-tone="attention">
          <span className="journey__legend-swatch" /> waiting
        </span>
        <span className="journey__legend-item" data-tone="critical">
          <span className="journey__legend-swatch" /> confirmed blocked
        </span>
        <span className="journey__legend-item" data-tone="neutral">
          <span className="journey__legend-swatch" /> upcoming
        </span>
        <span className="dim">
          Heavier line = critical path · dotted = dependency · dashed = handoff
        </span>
      </div>
    </div>
  );
}

/** Wraps a node title onto at most two lines, truncating only as a last resort. */
function wrap(value: string, chars: number): string[] {
  const limit = Math.max(9, Math.round(chars));
  if (value.length <= limit) return [value];
  const lines: string[] = [];
  let current = '';
  for (const word of value.split(' ')) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > limit && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
    if (lines.length === 2) break;
  }
  if (lines.length < 2 && current) lines.push(current);
  return lines
    .slice(0, 2)
    .map((line) => (line.length > limit + 3 ? `${line.slice(0, limit + 2)}…` : line));
}
